import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './TagsControls.module.scss';
import { getOptimalTextColor } from '../../../../../utils/colorHelper';
import cx from 'classnames';
import Menu from '../../../../../components/Menu/Menu';
import { CustomButton } from '../../../../../components/Button/Button';
import { HexColorInput, HexColorPicker } from 'react-colorful';
import { useTranslation } from 'react-i18next';
import TagsList from '../TagsList/TagsList';
import { clearEmojis, clearSpecialSymbols } from '../../../../../utils/stringHelper';
import SelectOptionTags from '../SelectOptionTags/SelectOptionTags';
import { ENTER_KEY_CODE, ESCAPE_KEY_CODE, SPACE_KEY_CODE } from '../../../../../constants/common';

const MAX_TAG_NAME_LENGTH = 30;

const TRANSLATE_OFFSET = 16;
const MENU_OFFSET = 32;

const TagsControls = ({ className = '', tags, editable = false, chooseFromOptions = [], onSaveTag, onSelectTag, onRemoveTag }) => {
  const [inputFocus, setInputFocus] = useState(false);
  const [tagName, setTagName] = useState('');
  const [tagColor, setTagColor] = useState('#eeeeee');
  const [saveTrigger, setSaveTrigger] = useState(false);
  const [transform, setTransform] = useState('');

  const { t } = useTranslation();

  const inputRef = useRef(null);
  const inputContainerRef = useRef(null);
  const menuRef = useRef(null);

  const availableTags = useMemo(() => chooseFromOptions.filter(option => !tags.some(tag => tag.id === option.id)), [
    tags,
    chooseFromOptions,
  ]);

  const options = useMemo(() => {
    if (editable) {
      return [];
    }

    return availableTags;
  }, [tagName, availableTags, chooseFromOptions]);

  const showOptions = useMemo(() => editable ? tagName.length : inputFocus, [editable, tagName, inputFocus]);

  const placeholder = useMemo(
    () => t(editable ? 'company.settings.tags.create_new.placeholder' : 'company.settings.tags.add.placeholder'),
    [editable, t]
  );

  const handleColorChange = color => {
    setTagColor(color);
  };

  const handleCancel = () => {
    setTagName('');
    setTagColor('#eeeeee');
  };

  const handleSave = useCallback(async () => {
    await onSaveTag(tagName, tagColor);
    setTagName('');
    setTagColor('#eeeeee');
  }, [tagName, tagColor]);

  const handleInputChanged = e => {
    setTagName(clearSpecialSymbols(clearEmojis(e.target.value)).toLowerCase());
  };

  const handleSelectTag = tagId => {
    inputRef.current.blur();
    onSelectTag(tagId);
    setTagName('');
    setTagColor('#eeeeee');
  };

  useEffect(() => {
    const handleKeyPress = e => {
      switch (e.keyCode) {
        case ESCAPE_KEY_CODE:
          handleCancel();
          break;
        case ENTER_KEY_CODE:
        case SPACE_KEY_CODE:
          setSaveTrigger(true);
          break;
        default:
          break;
      }
    };
    if (editable) {
      if (showOptions) {
        document.addEventListener('keydown', handleKeyPress);
      } else {
        document.removeEventListener('keydown', handleKeyPress);
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [showOptions]);

  useEffect(() => {
    if (saveTrigger) {
      handleSave();
      setSaveTrigger(false);
    }
  }, [saveTrigger]);

  useEffect(() => {
    if (menuRef?.current) {

      let transform = `translateY(${inputRef?.current.offsetHeight + TRANSLATE_OFFSET}px)`;
      if (inputRef?.current) {
        const { top } = inputRef?.current?.getBoundingClientRect();
        if (top + menuRef.current.offsetHeight + TRANSLATE_OFFSET + MENU_OFFSET > window.innerHeight) {
          transform = `translateY(calc(-100% - ${TRANSLATE_OFFSET}px))`;
        }
      }
      setTransform(transform);
    }
  }, [menuRef?.current?.offsetHeight, showOptions]);

  return (
    <ul
      className={cx(styles.wrapper, { [styles.inputFocus]: inputFocus }, className)}
      tabIndex={0}
      aria-label={placeholder}
    >
      <TagsList tags={tags} onRemove={onRemoveTag} />
      {editable || availableTags.length ? (
        <li
          ref={inputContainerRef}
          className={cx(styles.inputContainer)}
          style={{ backgroundColor: tagName ? tagColor : '', color: getOptimalTextColor(tagColor) }}
        >
          <input
            ref={inputRef}
            type="text"
            value={tagName}
            className={styles.input}
            size={Math.max(placeholder.length, tagName?.length)}
            placeholder={placeholder}
            onChange={handleInputChanged}
            onFocus={() => setInputFocus(true)}
            onBlur={() => setInputFocus(false)}
            maxLength={MAX_TAG_NAME_LENGTH}
            debounceTimeout={100}
          />
        </li>
      ) : null}
      {<li className={cx(styles.options, { [styles.show]: showOptions })}>
        {editable ? (
          <Menu
            customStyles={{
              top: inputContainerRef?.current?.offsetTop + 'px',
              left: inputContainerRef?.current?.offsetLeft + 'px',
              boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.2)',
              transform: transform,
            }}
            className={styles.saveMenu}
            ref={menuRef}
          >
            <h3 className={styles.title}>{t('company.settings.tags.create_new.title')}</h3>
            <HexColorPicker color={tagColor || '#eeeeee'} onChange={handleColorChange} />
            <HexColorInput alpha={true} prefixed={true} color={tagColor || '#eeeeee'} onChange={handleColorChange} />
            <div className={styles.btnContainer}>
              <CustomButton label={t('general.cancel')} variant="secondary" onClick={handleCancel} className={styles.button} />
              <CustomButton
                label={t('company.settings.tags.save_tag')}
                variant="primary"
                onClick={handleSave}
                className={styles.button}
                disabled={tags.find(tag => tag.name === tagName)}
              />
            </div>
          </Menu>
        ) : availableTags.length ? (
          <SelectOptionTags
            inputRef={inputContainerRef}
            tags={options}
            search={tagName}
            onSelect={handleSelectTag}
            style={{
              top: inputContainerRef?.current?.offsetTop + 'px',
              left: inputContainerRef?.current?.offsetLeft + 'px',
            }}
          />
        ) : null}
      </li>}
    </ul>
  );
};

export default TagsControls;
